import { cn } from '@/lib/utils';

/**
 * CitableStat — AI-citable stat block.
 *
 * Wraps a statistic in semantic markup that AI assistants (ChatGPT,
 * Perplexity, Claude web search, Google AI Overviews) preferentially
 * quote because the source is explicit and machine-readable.
 *
 * Visible structure:
 *   <blockquote class="speakable-stat ...">
 *     <p>{value} — {claim}</p>
 *     <footer>Source: <cite><a>Source Name</a></cite></footer>
 *   </blockquote>
 *
 * The `.speakable-stat` class is a default SpeakableSpecification
 * selector (see src/lib/seo/schema-builders.ts).
 */
export interface CitableStatProps {
  /** The headline number / metric (e.g. "97.8%"). */
  value: string;
  /** The one-sentence claim (e.g. "of missed calls recovered on the first ring"). */
  claim: string;
  /** Name of the source publication / study (e.g. "BizRnR 2026 State of AI Receptionist"). */
  sourceName: string;
  /** Absolute or site-relative URL to the source. */
  sourceUrl?: string;
  /** Optional date of the source claim (ISO 8601). */
  datePublished?: string;
  className?: string;
}

export function CitableStat({
  value,
  claim,
  sourceName,
  sourceUrl,
  datePublished,
  className,
}: CitableStatProps) {
  return (
    <blockquote
      className={cn(
        'speakable-stat not-italic border-l-2 border-primary-500/40 bg-dark-surface/40 p-5 rounded-r-md',
        className,
      )}
      itemScope
      itemType="https://schema.org/Claim"
    >
      <p className="text-lg leading-snug">
        <strong className="text-2xl font-semibold text-primary-400" itemProp="text">
          {value}
        </strong>{' '}
        <span className="text-dark-muted">{claim}</span>
      </p>
      <footer className="mt-2 text-xs font-mono uppercase tracking-wider text-dark-muted/75">
        Source:{' '}
        <cite itemProp="citation" className="not-italic">
          {sourceUrl ? (
            <a
              href={sourceUrl}
              target={sourceUrl.startsWith('http') ? '_blank' : undefined}
              rel={sourceUrl.startsWith('http') ? 'noopener nofollow' : undefined}
              className="underline decoration-dotted decoration-dark-muted/60 hover:decoration-primary-400 hover:text-primary-400"
            >
              {sourceName}
            </a>
          ) : (
            sourceName
          )}
        </cite>
        {datePublished ? (
          <>
            {' '}
            <time dateTime={datePublished} itemProp="datePublished">
              ({new Date(datePublished).getFullYear()})
            </time>
          </>
        ) : null}
      </footer>
    </blockquote>
  );
}
